'use client';
// Options-strategy payoff diagram. Steve directive 2026-05-09:
// "i want to see how this strategy protects me from my ownership of the
//  underlying. ie above a certain price im called out, but below a certain
//  price im protected. thats the chart i want to see"
//
// Renders TWO P&L curves at expiry:
//   1. Stock-only — what you'd make/lose if you did nothing (linear: p - spot)
//   2. With strategy — what you make/lose under the hedge (capped above the
//      call strike, floored below the put strike, etc.)
//
// Shaded zones between the two lines:
//   - Green where strategy > stock-alone  → protection / income gained
//   - Red where strategy < stock-alone    → upside given up (called away)

import { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  Area,
} from 'recharts';
import type { HedgeStrategy } from '@/lib/hedge-structures';

export function PayoffChart({ strategy }: { strategy: HedgeStrategy }) {
  const spot = strategy.spot;

  // Build a dense series with both P&L curves (stock-alone + with-strategy).
  // The strategy's own scenarios already give the combined P&L (stock owned
  // PLUS the strategy legs) — see lib/hedge-structures.ts buildCoveredCall etc.
  const series = useMemo(() => {
    const scenarios = strategy.scenarios ?? [];
    if (scenarios.length < 2) return [];
    const minP = scenarios[0].priceAtExpiry;
    const maxP = scenarios[scenarios.length - 1].priceAtExpiry;
    const N = 80;
    const out: Array<{
      price: number;
      stock: number;
      strategy: number;
      protection: [number, number];
      gaveUp: [number, number];
    }> = [];
    for (let i = 0; i <= N; i++) {
      const p = minP + (maxP - minP) * (i / N);
      let lo = 0;
      for (let j = 0; j < scenarios.length - 1; j++) {
        if (scenarios[j].priceAtExpiry <= p && scenarios[j + 1].priceAtExpiry >= p) { lo = j; break; }
      }
      const a = scenarios[lo];
      const b = scenarios[Math.min(lo + 1, scenarios.length - 1)];
      const t = b.priceAtExpiry === a.priceAtExpiry ? 0 : (p - a.priceAtExpiry) / (b.priceAtExpiry - a.priceAtExpiry);
      const strategyPnl = a.pnlPerShare + t * (b.pnlPerShare - a.pnlPerShare);
      const stockPnl = p - spot;
      // Recharts ranged areas use [low, high]. Draw protection zone (green)
      // only where strategy > stock; gave-up zone (red) only where strategy < stock.
      const protection: [number, number] = strategyPnl > stockPnl ? [stockPnl, strategyPnl] : [0, 0];
      const gaveUp: [number, number] = strategyPnl < stockPnl ? [strategyPnl, stockPnl] : [0, 0];
      out.push({ price: p, stock: stockPnl, strategy: strategyPnl, protection, gaveUp });
    }
    return out;
  }, [strategy.scenarios, spot]);

  if (series.length === 0) {
    return <div className="text-[10px] text-[#64748b]">no payoff scenarios for this strategy</div>;
  }

  const allYs = series.flatMap((s) => [s.stock, s.strategy]);
  const minY = Math.min(...allYs);
  const maxY = Math.max(...allYs);
  const yPad = Math.max(0.5, (maxY - minY) * 0.1);
  const yMin = minY - yPad;
  const yMax = maxY + yPad;

  const strikes = strategy.legs
    .filter((l) => (l.asset === 'call' || l.asset === 'put') && l.strike != null)
    .map((l) => ({ strike: l.strike!, asset: l.asset, action: l.action }));

  // Floor (max protection) = max strategy-pnl below spot; ceiling (called-out)
  // = max strategy-pnl above spot. Used for the labeled summary at top right.
  const belowSpot = series.filter((s) => s.price <= spot);
  const aboveSpot = series.filter((s) => s.price >= spot);
  const protectionFloor = belowSpot.length ? Math.min(...belowSpot.map((s) => s.strategy)) : Math.min(...series.map((s) => s.strategy));
  const calledOutCeiling = aboveSpot.length ? Math.max(...aboveSpot.map((s) => s.strategy)) : Math.max(...series.map((s) => s.strategy));

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[#64748b]">
          Payoff at expiry — your position with vs without the strategy
        </div>
        <div className="text-[9px] text-[#94a3b8] tabular-nums">
          floor <span className="text-emerald-300 font-semibold">{protectionFloor >= 0 ? '+' : ''}${protectionFloor.toFixed(2)}</span>
          {' · '}ceiling <span className="text-amber-300 font-semibold">{calledOutCeiling >= 0 ? '+' : ''}${calledOutCeiling.toFixed(2)}</span>
        </div>
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 18, right: 24, bottom: 4, left: 0 }}>
            <XAxis
              type="number"
              dataKey="price"
              domain={[series[0].price, series[series.length - 1].price]}
              tick={{ fill: '#64748b', fontSize: 9 }}
              tickFormatter={(v: number) => `$${v.toFixed(0)}`}
              stroke="#475569"
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 9 }}
              domain={[yMin, yMax]}
              tickFormatter={(v: number) => `${v >= 0 ? '+' : ''}$${v.toFixed(0)}`}
              stroke="#475569"
              width={50}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#0a0e14', border: '1px solid #334155', borderRadius: 6, fontSize: 11, color: '#e2e8f0' }}
              labelStyle={{ color: '#94a3b8' }}
              labelFormatter={(v: unknown) => `Stock @ $${Number(v).toFixed(2)}`}
              formatter={(v: unknown, name: unknown) => {
                const label = String(name ?? '');
                if (label === 'protection' || label === 'gaveUp') return [null, null] as unknown as [string, string];
                const num = Number(v);
                return [`${num >= 0 ? '+' : ''}$${num.toFixed(2)}/sh`, label === 'stock' ? 'Stock alone' : 'With strategy'];
              }}
            />
            {/* Zero line */}
            <ReferenceLine y={0} stroke="#475569" strokeWidth={1} />
            {/* Strike vertical lines (kept for context — labeled clearly above the plot) */}
            {strikes.map((s, i) => {
              const isCall = s.asset === 'call';
              const color = isCall ? '#fbbf24' : '#10b981';
              const verb = isCall && s.action === 'sell' ? 'CALLED OUT' : (!isCall && s.action === 'buy' ? 'PROTECTED' : `${s.action.toUpperCase()} ${isCall ? 'C' : 'P'}`);
              return (
                <ReferenceLine
                  key={`strike-${i}`}
                  x={s.strike}
                  stroke={color}
                  strokeDasharray="3 3"
                  label={{ value: `${verb} $${s.strike}`, fill: color, fontSize: 10, fontWeight: 600, position: 'top' }}
                />
              );
            })}
            {/* Spot vertical line */}
            {spot != null && (
              <ReferenceLine
                x={spot}
                stroke="#c9a84c"
                strokeDasharray="2 2"
                label={{ value: `spot $${spot.toFixed(2)}`, fill: '#c9a84c', fontSize: 10, fontWeight: 600, position: 'insideTopRight' }}
              />
            )}
            {/* Ranged-area shading: protection (green) where strategy > stock,
                gave-up (red) where strategy < stock. Drawn first so the lines
                paint on top. */}
            <Area type="monotone" dataKey="protection" stroke="none" fill="#10b981" fillOpacity={0.22} isAnimationActive={false} legendType="none" />
            <Area type="monotone" dataKey="gaveUp" stroke="none" fill="#ef4444" fillOpacity={0.22} isAnimationActive={false} legendType="none" />
            {/* Stock-alone curve (gray dashed — the "do nothing" baseline) */}
            <Line type="linear" dataKey="stock" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            {/* With-strategy curve (white solid — what the hedge actually does) */}
            <Line type="monotone" dataKey="strategy" stroke="#e2e8f0" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[#cbd5e1] mt-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-px border-t-2 border-dashed" style={{ borderColor: '#94a3b8' }} />
          stock alone (no hedge)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-0.5" style={{ backgroundColor: '#e2e8f0' }} />
          with strategy
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-2.5" style={{ backgroundColor: '#10b981', opacity: 0.5 }} />
          protected (strategy &gt; stock)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-2.5" style={{ backgroundColor: '#ef4444', opacity: 0.5 }} />
          gave up upside (called out)
        </span>
      </div>
    </div>
  );
}
